import { SEO_CONFIG } from './seo';

// Organization Schema for Piper Card
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SEO_CONFIG.siteName,
  alternateName: 'Piper',
  url: SEO_CONFIG.siteUrl,
  logo: `${SEO_CONFIG.siteUrl}/logo.png`,
  description: SEO_CONFIG.defaultDescription,
  knowsAbout: [
    'Digital Business Cards',
    'CRM Software',
    'Sales Pipeline Management',
    'Team Collaboration',
    'Contact Management',
    'Lead Tracking'
  ],
  sameAs: [
    // Add social media profiles when available
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Support',
    availableLanguage: ['English']
  }
});

// WebSite Schema with Search Action
export const getWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SEO_CONFIG.siteName,
  url: SEO_CONFIG.siteUrl,
  description: SEO_CONFIG.defaultDescription,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SEO_CONFIG.siteUrl}/discover?q={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  }
});

// Person Schema for individual cards
export const getPersonSchema = (card) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: card.name || 'User',
    url: `${SEO_CONFIG.siteUrl}/card/${card.username}`,
  };

  // Add optional fields if available
  if (card.email) {
    schema.email = card.email;
  }
  
  if (card.phone) {
    schema.telephone = card.phone;
  }
  
  if (card.profileImage) {
    schema.image = card.profileImage;
  }
  
  if (card.bio) {
    schema.description = card.bio;
  }
  
  if (card.title) {
    schema.jobTitle = card.title;
  }
  
  if (card.company) {
    schema.worksFor = {
      '@type': 'Organization',
      name: card.company
    };
  }
  
  if (card.location) {
    schema.address = {
      '@type': 'PostalAddress',
      addressLocality: card.location
    };
  }

  // Add social media profiles
  const sameAs = [];
  if (card.linkedin) sameAs.push(card.linkedin);
  if (card.twitter) sameAs.push(card.twitter);
  if (card.github) sameAs.push(card.github);
  if (card.website) sameAs.push(card.website);
  if (card.instagram) sameAs.push(card.instagram);
  if (card.facebook) sameAs.push(card.facebook);
  
  if (sameAs.length > 0) {
    schema.sameAs = sameAs;
  }

  return schema;
};

// BreadcrumbList Schema
export const getBreadcrumbSchema = (breadcrumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: `${SEO_CONFIG.siteUrl}${crumb.path}`
  }))
});

// ProfilePage Schema
export const getProfilePageSchema = (card) => ({
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  name: `${card.name || 'User'} - Digital Business Card`,
  description: card.bio || 'Digital business card on Piper Card',
  url: `${SEO_CONFIG.siteUrl}/card/${card.username}`,
  mainEntity: getPersonSchema(card)
});

// ContactPoint Schema for business cards
export const getContactPointSchema = (card) => {
  const contactPoints = [];
  
  if (card.email) {
    contactPoints.push({
      '@type': 'ContactPoint',
      contactType: 'Email',
      email: card.email
    });
  }
  
  if (card.phone) {
    contactPoints.push({
      '@type': 'ContactPoint',
      contactType: 'Phone',
      telephone: card.phone
    });
  }
  
  return contactPoints;
};

// Combined schema for card pages (most comprehensive)
export const getCardPageSchema = (card) => {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      getPersonSchema(card),
      getProfilePageSchema(card),
      getBreadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Cards', path: '/discover' },
        { name: card.name || 'User', path: `/card/${card.username}` }
      ])
    ]
  };
};
